import { useState } from 'react'
import { Badge } from '../catalyst/badge'
import { Button } from '../catalyst/button'
import { Field, Label } from '../catalyst/fieldset'
import { Subheading } from '../catalyst/heading'
import { Text } from '../catalyst/text'
import { Textarea } from '../catalyst/textarea'
import type { ReviewStatus, Revision } from '../lib/types'

const STATUS: Record<ReviewStatus, { label: string; color: 'amber' | 'green' | 'red' }> = {
  unreviewed: { label: 'Needs review', color: 'amber' },
  approved: { label: 'Reviewed', color: 'green' },
  rejected: { label: 'Rejected', color: 'red' },
}

export function ReviewBadge({ status }: { status: ReviewStatus }) {
  return <Badge color={STATUS[status].color}>{STATUS[status].label}</Badge>
}

/**
 * The sign-off box. Approving says "I checked this against the source"; rejecting needs a reason.
 * Editing the content (the form above it) drops an approved record back to "Needs review" on the
 * server, so a sign-off is always about the content as saved.
 */
export function ReviewPanel({
  status,
  notes,
  reviewedAt,
  busy,
  onReview,
}: {
  status: ReviewStatus
  notes: string | null
  reviewedAt: string | null
  busy: boolean
  onReview: (review: { status: ReviewStatus; notes: string | null }) => void
}) {
  const [text, setText] = useState(notes ?? '')
  const needsReason = status !== 'rejected' && text.trim() === ''
  return (
    <section className="rounded-lg border border-zinc-950/10 p-5 dark:border-white/10">
      <div className="flex items-center gap-3">
        <Subheading>Review</Subheading>
        <ReviewBadge status={status} />
        {reviewedAt && <Text className="!mt-0">{new Date(reviewedAt).toLocaleString()}</Text>}
      </div>
      <Field className="mt-4">
        <Label>Notes</Label>
        <Textarea rows={2} value={text} onChange={(event) => setText(event.target.value)} placeholder="What you checked, or why it is wrong." />
      </Field>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button disabled={busy || status === 'approved'} onClick={() => onReview({ status: 'approved', notes: text.trim() || null })}>
          Mark reviewed
        </Button>
        <Button outline disabled={busy || status === 'unreviewed'} onClick={() => onReview({ status: 'unreviewed', notes: text.trim() || null })}>
          Needs review
        </Button>
        <Button outline disabled={busy || status === 'rejected' || needsReason} onClick={() => onReview({ status: 'rejected', notes: text.trim() })}>
          Reject
        </Button>
      </div>
      {status !== 'rejected' && <Text className="mt-2 text-xs">Rejecting needs a note saying why.</Text>}
    </section>
  )
}

function show(value: unknown) {
  if (value === null || value === undefined || value === '') return '—'
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

export function HistoryList({ history }: { history: Revision[] }) {
  return (
    <section>
      <Subheading>History</Subheading>
      {history.length === 0 ? (
        <Text className="mt-2">No staff changes yet. This is the data as first loaded.</Text>
      ) : (
        <ul className="mt-3 divide-y divide-zinc-950/5 dark:divide-white/10">
          {history.map((r) => (
            <li key={r.id} className="py-3 text-sm/6">
              <div className="flex flex-wrap items-center gap-2 text-zinc-500 dark:text-zinc-400">
                <span className="font-medium text-zinc-950 dark:text-white">{r.actorName ?? 'Someone'}</span>
                <span>{r.action === 'review' ? 'reviewed' : r.action === 'edit' ? 'edited' : 'edited and reviewed'}</span>
                <span>· {new Date(r.createdAt).toLocaleString()}</span>
              </div>
              <ul className="mt-1 space-y-0.5">
                {Object.keys({ ...r.before, ...r.after }).map((key) => (
                  <li key={key} className="text-zinc-700 dark:text-zinc-300">
                    <span className="font-medium">{key.replace(/^usePattern:.*/, 'use pattern')}</span>: <span className="text-zinc-500 line-through">{show(r.before[key])}</span> → {show(r.after[key])}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
